import type { DispatchChild, DispatchChildStatus } from '@/features/agent-dispatch/types';
import type { ProvenanceManifest, ProvenanceSource } from '@/features/dossier/api';

export interface CountryGroup {
  country: string | null;
  runs: DispatchChild[];
}

/** Group dispatch runs by country, preserving first-seen order. A country with
 *  multiple runs (EU's complete-dossier annexes) becomes one group. */
export function groupRunsByCountry(runs: DispatchChild[]): CountryGroup[] {
  const order: (string | null)[] = [];
  const map = new Map<string | null, DispatchChild[]>();
  for (const r of runs) {
    if (!map.has(r.country)) {
      map.set(r.country, []);
      order.push(r.country);
    }
    map.get(r.country)!.push(r);
  }
  return order.map((c) => ({ country: c, runs: map.get(c)! }));
}

/** A country tab's id: its group's first run. The ONE place this rule lives (the tab rows and
 *  the link lookup below both use it). */
export function tabIdOf(group: CountryGroup): number {
  return group.runs[0]!.id;
}

/** The tab to open for a linked run: the id of its country's tab, so a link to any EU part (PMS,
 *  SSCP) opens the one EU tab. Null when no run is linked or it is not in this dispatch (the tabs
 *  then open as they always did). */
export function tabRunIdFor(groups: CountryGroup[], runId: number | null): number | null {
  if (runId === null) return null;
  const group = groups.find((g) => g.runs.some((r) => r.id === runId));
  return group ? tabIdOf(group) : null;
}

/** Aggregate a country's part-run statuses into one tab status: in-progress
 *  wins (running > queued), then failed, else completed.
 *  Same rule as backend/services/dossier/results.py::aggregate_status; keep them in step. */
export function aggregateStatus(statuses: DispatchChildStatus[]): DispatchChildStatus {
  if (statuses.some((s) => s === 'running')) return 'running';
  if (statuses.some((s) => s === 'queued')) return 'queued';
  if (statuses.some((s) => s === 'failed')) return 'failed';
  return 'completed';
}

/** Merge several annex manifests into one "full dossier" matrix: union the
 *  evidence arrays (sources deduped by source_id), keep the first annex's
 *  framework / run / coverage / statement_coverage / defense (a pragmatic
 *  approximation — full per-annex defense merge is future work). */
export function mergeProvenanceManifests(
  manifests: (ProvenanceManifest | null | undefined)[],
): ProvenanceManifest | null {
  const present = manifests.filter((m): m is ProvenanceManifest => m != null);
  if (present.length === 0) return null;
  if (present.length === 1) return present[0]!;
  const first = present[0]!;
  const sourcesById = new Map<string, ProvenanceSource>();
  for (const m of present) {
    for (const s of m.sources) {
      if (!sourcesById.has(s.source_id)) sourcesById.set(s.source_id, s);
    }
  }
  return {
    ...first,
    sources: [...sourcesById.values()],
    claims: present.flatMap((m) => m.claims),
    section_links: present.flatMap((m) => m.section_links),
    unsourced: present.flatMap((m) => m.unsourced ?? []),
  };
}
